//frontend/src/components/UserProfile.js
import React, { useState, useEffect } from 'react';
import { 
  FaTimes, FaUser, FaEnvelope, FaPhone, FaLock, 
  FaSave, FaUpload, FaEye, FaEyeSlash,
  FaCheckCircle, FaTimesCircle
} from 'react-icons/fa';
import axiosInstance from '../axios';
import '../styles/admin.css';

const UserProfile = ({ onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [profileData, setProfileData] = useState({
    fullname: '',
    email: '',
    utas: '',
    image: '',
    username: '',
    role: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axiosInstance.get('/api/users/me');
      if (response.data.success) {
        const user = response.data.data;
        setProfileData({
          fullname: user.fullname || '',
          email: user.email || '',
          utas: user.utas || '',
          image: user.image || '',
          username: user.username || '',
          role: user.role || ''
        });
      }
    } catch (error) {
      console.error('Fetch profile error:', error);
      alert('Профайл татахад алдаа гарлаа');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert('Зөвхөн зураг файл сонгоно уу!');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      alert('Зургийн хэмжээ 2MB-с бага байх ёстой!');
      return;
    }
    
    setUploadingImage(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);
      
      const response = await axiosInstance.post('/api/upload-image', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setProfileData(prev => ({ ...prev, image: response.data.imageUrl }));
      }
    } catch (error) {
      console.error('Image upload error:', error);
      alert(error.response?.data?.msg || 'Зураг хуулахад алдаа гарлаа');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = async () => {
    if (!profileData.image) return;
    
    try {
      await axiosInstance.delete('/api/delete-image', { 
        data: { imageUrl: profileData.image } 
      });
    } catch (error) {
      console.error('Image delete error:', error);
    }
    
    setProfileData(prev => ({ ...prev, image: '' }));
  };

  const handleUpdateProfile = async () => {
    const newErrors = {};
    
    if (!profileData.fullname.trim()) {
      newErrors.fullname = 'Бүтэн нэр оруулна уу';
    } else if (profileData.fullname.trim().length < 2) {
      newErrors.fullname = 'Бүтэн нэр 2-оос дээш тэмдэгт байх ёстой';
    }
    
    if (!profileData.email.trim()) {
      newErrors.email = 'И-мэйл оруулна уу';
    } else if (!/\S+@\S+\.\S+/.test(profileData.email)) {
      newErrors.email = 'И-мэйл хаяг буруу байна';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axiosInstance.put('/api/users/me/profile', {
        fullname: profileData.fullname.trim(),
        email: profileData.email.trim(),
        utas: profileData.utas.trim(),
        image: profileData.image
      });
      
      if (response.data.success) {
        alert('✅ Профайл амжилттай шинэчлэгдлээ');
        setErrors({});
        if (onSave) onSave();
      }
    } catch (error) {
      console.error('Update profile error:', error);
      alert('❌ ' + (error.response?.data?.msg || 'Профайл шинэчлэхэд алдаа гарлаа'));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    const newErrors = {};
    
    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Одоогийн нууц үг оруулна уу';
    }
    
    if (!passwordData.newPassword) {
      newErrors.newPassword = 'Шинэ нууц үг оруулна уу';
    } else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = 'Шинэ нууц үг 6-аас дээш тэмдэгт байх ёстой';
    }
    
    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Нууц үг давтах шаардлагатай';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Нууц үг таарахгүй байна';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axiosInstance.put('/api/users/me/password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      if (response.data.success) {
        alert('✅ Нууц үг амжилттай солигдлоо');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setErrors({});
      }
    } catch (error) {
      console.error('Change password error:', error);
      alert('❌ ' + (error.response?.data?.msg || 'Нууц үг солихад алдаа гарлаа'));
    } finally {
      setLoading(false);
    }
  };

  const getRoleText = (role) => {
    const texts = {
      system_admin: 'Системийн админ',
      staff: 'Ажилтан',
      user: 'Хэрэглэгч'
    };
    return texts[role] || role;
  };

  return (
    <div className="modal-overlay-enhanced animate-fadeIn" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="modal-content-enhanced max-w-2xl w-full animate-slideUp" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-enhanced bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-white bg-opacity-20 p-3 rounded-xl backdrop-blur-sm">
                <FaUser className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">Миний профайл</h3>
                <p className="text-blue-100 text-sm mt-1">
                  {profileData.username} ({getRoleText(profileData.role)})
                </p>
              </div>
            </div>
            <button 
              type="button"
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-xl p-3 transition-all duration-200 hover:rotate-90"
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex border-b bg-gray-50">
          <button
            onClick={() => {
              setActiveTab('info');
              setErrors({});
            }}
            className={`flex-1 px-6 py-4 font-semibold transition-all ${
              activeTab === 'info'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-600 hover:text-blue-600 hover:bg-gray-100'
            }`}
          >
            <FaUser className="inline mr-2" />
            Хувийн мэдээлэл
          </button>
          <button
            onClick={() => {
              setActiveTab('password');
              setErrors({});
            }}
            className={`flex-1 px-6 py-4 font-semibold transition-all ${
              activeTab === 'password'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-600 hover:text-blue-600 hover:bg-gray-100'
            }`}
          >
            <FaLock className="inline mr-2" />
            Нууц үг солих
          </button>
        </div>

        <div className="modal-body-enhanced max-h-[65vh] overflow-y-auto p-6 bg-gradient-to-br from-gray-50 to-blue-50">
          {activeTab === 'info' ? (
            <div className="space-y-6">
              <div className="flex flex-col items-center">
                {profileData.image ? (
                  <div className="relative inline-block">
                    <img
                      src={`${window.location.origin}${profileData.image}`}
                      alt="Profile"
                      className="w-32 h-32 object-cover rounded-full border-4 border-blue-200 shadow-lg"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/128?text=No+Image';
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-all shadow-md"
                      title="Зураг устгах"
                    >
                      <FaTimes className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-32 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full border-4 border-blue-200 flex items-center justify-center text-white text-5xl font-bold shadow-lg">
                    {profileData.fullname.charAt(0).toUpperCase()}
                  </div>
                )}
                
                <label className="mt-4 cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                  <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all">
                    {uploadingImage ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Хуулж байна...
                      </>
                    ) : (
                      <>
                        <FaUpload className="mr-2" />
                        Зураг солих
                      </>
                    )}
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  JPG, PNG, GIF (Max 2MB)
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="form-label">
                    <FaUser className="inline mr-2 text-blue-600" />
                    Бүтэн нэр <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-input-enhanced ${errors.fullname ? 'border-red-400 bg-red-50' : ''}`}
                    placeholder="Овог нэр"
                    value={profileData.fullname}
                    onChange={(e) => setProfileData(prev => ({ ...prev, fullname: e.target.value }))}
                  />
                  {errors.fullname && (
                    <p className="error-message animate-slideDown">
                      <FaTimesCircle className="mr-1" />
                      {errors.fullname}
                    </p>
                  )}
                </div>

                <div>
                  <label className="form-label">
                    <FaEnvelope className="inline mr-2 text-blue-600" />
                    И-мэйл хаяг <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    className={`form-input-enhanced ${errors.email ? 'border-red-400 bg-red-50' : ''}`}
                    placeholder="example@email.com"
                    value={profileData.email}
                    onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                  />
                  {errors.email && (
                    <p className="error-message animate-slideDown">
                      <FaTimesCircle className="mr-1" />
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="form-label">
                    <FaPhone className="inline mr-2 text-blue-600" />
                    Утасны дугаар
                  </label>
                  <input
                    type="text"
                    className="form-input-enhanced"
                    placeholder="99119911"
                    value={profileData.utas}
                    onChange={(e) => setProfileData(prev => ({ ...prev, utas: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <FaLock className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      Нууц үгээ солихдоо аюулгүй байдлын дүрэм баримтална уу. Нууц үг 6-аас дээш тэмдэгт байх ёстой.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="form-label">
                  <FaLock className="inline mr-2 text-blue-600" />
                  Одоогийн нууц үг <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    className={`form-input-enhanced pr-12 ${errors.currentPassword ? 'border-red-400 bg-red-50' : ''}`}
                    placeholder="••••••••"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-all"
                  >
                    {showCurrentPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.currentPassword && (
                  <p className="error-message animate-slideDown">
                    <FaTimesCircle className="mr-1" />
                    {errors.currentPassword}
                  </p>
                )}
              </div>

              <div>
                <label className="form-label">
                  <FaLock className="inline mr-2 text-blue-600" />
                  Шинэ нууц үг <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    className={`form-input-enhanced pr-12 ${errors.newPassword ? 'border-red-400 bg-red-50' : ''}`}
                    placeholder="••••••••"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-all"
                  >
                    {showNewPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="error-message animate-slideDown">
                    <FaTimesCircle className="mr-1" />
                    {errors.newPassword}
                  </p>
                )}
              </div>

              <div>
                <label className="form-label">
                  <FaCheckCircle className="inline mr-2 text-blue-600" />
                  Шинэ нууц үг давтах <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={`form-input-enhanced pr-12 ${errors.confirmPassword ? 'border-red-400 bg-red-50' : ''}`}
                    placeholder="••••••••"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-all"
                  >
                    {showConfirmPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="error-message animate-slideDown">
                    <FaTimesCircle className="mr-1" />
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer-enhanced bg-gradient-to-r from-gray-50 to-blue-50 border-t-2 border-gray-200">
          <button 
            type="button"
            onClick={onClose}
            className="btn-secondary-enhanced"
            disabled={loading}
          >
            <FaTimes className="mr-2" />
            Хаах
          </button>
          <button 
            type="button"
            onClick={activeTab === 'info' ? handleUpdateProfile : handleChangePassword}
            className="btn-primary-enhanced"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                Хадгалж байна...
              </>
            ) : (
              <>
                <FaSave className="mr-2" />
                Хадгалах
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;